const IV_LENGTH = 16; // For AES, this is always 16
const SALT_LENGTH = 64;
const ITERATIONS = 100000;

interface EncryptedData {
    encrypted: string;  // Base64 encoded encrypted data
    iv: string;        // Base64 encoded initialization vector
    salt: string;      // Base64 encoded salt
    tag: string;       // Base64 encoded auth tag
}

/**
 * Helper function to convert ArrayBuffer to Base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

/**
 * Helper function to convert Base64 string to Uint8Array
 */
function base64ToUint8Array(base64: string): Uint8Array {
    return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
}

/**
 * Encrypts a string using AES-256-GCM in the browser
 * @param text - The text to encrypt
 * @param secretKey - The secret key used for encryption
 * @returns Object containing the encrypted data and necessary parameters
 */
export async function encrypt(text: string, secretKey: string): Promise<EncryptedData> {
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));

    // Convert key to crypto key
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(secretKey),
        'PBKDF2',
        false,
        ['deriveBits', 'deriveKey']
    );

    const key = await crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: salt,
            iterations: ITERATIONS,
            hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt']
    );

    const encrypted = await crypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv: iv
        },
        key,
        new TextEncoder().encode(text)
    );

    // Use helper function for base64 conversion
    return {
        encrypted: arrayBufferToBase64(encrypted),
        iv: arrayBufferToBase64(iv),
        salt: arrayBufferToBase64(salt),
        tag: '' // Note: In Web Crypto API, the auth tag is included in the encrypted data
    };
}

/**
 * Decrypts an encrypted string using AES-256-GCM in the browser
 * @param encryptedData - Object containing the encrypted data and parameters
 * @param secretKey - The secret key used for decryption
 * @returns The decrypted string
 */
export async function decrypt(encryptedData: EncryptedData, secretKey: string): Promise<string> {
    // Use helper function for base64 conversion
    const iv = base64ToUint8Array(encryptedData.iv);
    const salt = base64ToUint8Array(encryptedData.salt);
    const encrypted = base64ToUint8Array(encryptedData.encrypted);

    // Import the secret key
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(secretKey),
        'PBKDF2',
        false,
        ['deriveBits', 'deriveKey']
    );

    const key = await crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: salt,
            iterations: ITERATIONS,
            hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt']
    );

    const decrypted = await crypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: iv
        },
        key,
        encrypted
    );

    return new TextDecoder().decode(decrypted);
}
